import { CustomEventWithDetail, html, on, View, UnsafeHtml } from 'rune-ts';
import klass from './CreatorSummary.module.scss';
import { classes, htmlIf } from '../../../../shared/util';
import { CreatorTypeS } from '../../../../features/Creator/outbound/share';
import { getResizedUrl } from '../../../../shared/util/image';
import { MShopAppStoreCommunityConstantS } from '../../../../../../modules/MShop/App/Store/Community/S/Constant/module/MShopAppStoreCommunityConstantS';
import { font, staticFont } from '../../../../shared/typography';
import { getResizedImage70WebpUrl } from '../../../../shared/util/getResizedImageUrl';
import { makeDefaultProfileImg } from '../../../../shared/util/helper';
import { dataStr } from '../../../../shared/util/dataStr';
import { makeEmptyScreenNavigate } from '../../../../shared/app/navigate';
import { Users } from '../Users/Users';
import type {
  GetCartSummaryByCreatorResult,
  GetCartUsersForCreatorResult,
} from '../../../../features/Cart/repositories/results';
import { ButtonFollow } from '../../atoms/ButtonFollow/ButtonFollow';
import { CreatorFilterType, IStoreRanking } from '@services/MarppleShop/features/Creator/type';
import { RankDiff } from '@services/MarppleShop/renderApp/components/atoms/RankDiff/RankDiff.ts';
import { commify } from '@services/MarppleShop/shared/util/commify.ts';
import { humanReadableNumberV2 } from '../../../../../../shared/utils/formatter';
import { join, pipe, values } from '@fxts/core';
import { FanCountView } from '@services/MarppleShop/renderApp/components/cells/CreatorMainProfile/CreatorMainProfileView.ts';
import { unEscaper } from '../../../../../../shared/utils/escape.ts';
import { CreatorNameView } from '@services/MarppleShop/renderApp/components/cells/CreatorName/CreatorNameView.ts';

type CreatorSummaryMode = 'compact' | 'normal' | 'backdrop';

export type CreatorSummaryProps = CreatorTypeS.WeeklyPopularCreatorData['creators'][number] & {
  id: number;
  is_partner: boolean;
  fan_letter_count?: number;
  banner_img?: string;
  cart_summary?: GetCartSummaryByCreatorResult & Partial<GetCartUsersForCreatorResult>;
  is_liked?: boolean;
  is_login?: boolean;
  mode?: CreatorSummaryMode;
  like_count?: number;
  filter: CreatorFilterType;
  ranking: IStoreRanking | null;
  is_mobile?: boolean;
};

export class CreatorSummaryUpdateCartEvent extends CustomEventWithDetail<
  GetCartSummaryByCreatorResult & { user: GetCartUsersForCreatorResult['users'][number] | null }
> {}

class BaseCreatorSummary extends View<CreatorSummaryProps> {
  get isCount() {
    return this.data.cart_summary?.cart_product_count && this.data.cart_summary?.cart_user_count;
  }

  get defaultImg() {
    return makeDefaultProfileImg(300);
  }

  _makeCreatorSummaryInfoText(): UnsafeHtml {
    return html.preventEscape(``);
  }

  /**
   * 장바구니에 상품을 담았을 때, 유저 수와 상품 수를 업데이트
   */
  updateCreatorSummaryInfo(cart_product_count: number, cart_user_count: number) {
    const before_update_is_count = this.isCount;

    if (!this.data.cart_summary) {
      this.data.cart_summary = {
        cart_product_count,
        cart_user_count,
      };
    } else {
      this.data.cart_summary.cart_user_count += cart_user_count;
      this.data.cart_summary.cart_product_count += cart_product_count;
    }

    const $cart = this.element()?.querySelector(`.${klass.creator_fan_intersection_cart}`);

    if (!before_update_is_count && $cart)
      return ($cart.innerHTML = this._makeCreatorSummaryInfoText().toString());

    const $user_count = this.element()?.querySelector(`[data-user-count]`);
    const $product_count = this.element()?.querySelector(`[data-product-count]`);

    if ($user_count)
      $user_count.textContent = pipe(
        humanReadableNumberV2.to(this.data.cart_summary.cart_user_count, ET.lang === 'kr'),
        values,
        join(''),
      );

    if ($product_count)
      $product_count.textContent = pipe(
        humanReadableNumberV2.to(this.data.cart_summary.cart_product_count, ET.lang === 'kr'),
        values,
        join(''),
      );
  }
}

export class CompactCreatorSummary extends BaseCreatorSummary {
  private users: Users;

  constructor(props: CreatorSummaryProps) {
    super(props);

    this.users = new Users({ users: this.data.cart_summary?.users || [] });
  }

  protected override template() {
    return html`
      <div class="${klass.compact_creator_fan_intersection}">
        ${this.users} ${this.makeCreatorSummaryInfoHtml()}
      </div>
    `;
  }

  @on(CreatorSummaryUpdateCartEvent)
  update({ detail }: CreatorSummaryUpdateCartEvent) {
    const { cart_product_count, cart_user_count, user } = detail;

    this.updateCreatorSummaryInfo(cart_product_count, cart_user_count);

    // 슬라이드 애니메이션이 작동 실행 중일 때, 슬라이드 순서를 JS로 바꾸면 애니메이션 처리가 어색한 부분이 존재
    if (this.users.data.users.length < 3) {
      user && this.users.create([user]);
      this.users.redraw();
    }
  }

  makeCreatorSummaryInfoHtml() {
    return html`
      <div
        class="${classes(klass.creator_fan_intersection_cart, klass.none, font('14_medium', '12_medium'))}"
      >
        ${this._makeCreatorSummaryInfoText()}
      </div>
    `;
  }

  override _makeCreatorSummaryInfoText() {
    return this.isCount
      ? html.preventEscape(
          ET('mps2::creator_summary::compact', {
            cart_user_count: pipe(
              humanReadableNumberV2.to(this.data.cart_summary?.cart_user_count || 0, ET.lang === 'kr'),
              values,
              join(''),
            ),
            cart_product_count: pipe(
              humanReadableNumberV2.to(this.data.cart_summary?.cart_product_count || 0, ET.lang === 'kr'),
              values,
              join(''),
            ),
            bold: font('14_bold', '12_bold'),
          }),
        )
      : html.preventEscape(
          ET('mps2::creator_summary::not_cart_summary', {
            name: this.data.name,
            bold: font('14_bold', '12_bold'),
          }),
        );
  }
}

export class CreatorSummary extends BaseCreatorSummary {
  private button: ButtonFollow;
  private counter: FanCountView;

  constructor(props: CreatorSummaryProps) {
    super({ ...props });

    this.button = new ButtonFollow({
      withLabel: false,
      store_id: this.data.id,
      is_follow: !!this.data.is_liked,
      is_login: !!this.data.is_login,
      is_transparent: this.data.mode == 'backdrop',
    });
    this.counter = new FanCountView({
      like_count: this.data.like_count || 0,
      font: font('14_medium', '12_medium'),
    });
  }

  public getFanCountView(): FanCountView {
    return this.counter;
  }

  public getButtonFollow(): ButtonFollow {
    return this.button;
  }

  getColorModeKlass(): [string, string, string] {
    const { mode = 'normal' } = this.data;
    if (mode === 'normal') return [klass.blue, klass.blue, klass.normal];
    return [klass.white, klass.bright_70, klass.backdrop];
  }

  protected override template() {
    return this._makeCreatorSummaryHtml();
  }

  @on(CreatorSummaryUpdateCartEvent)
  update({ detail }: CreatorSummaryUpdateCartEvent) {
    const { cart_product_count, cart_user_count } = detail;

    this.updateCreatorSummaryInfo(cart_product_count, cart_user_count);
  }

  private _makeCreatorSummaryHtml() {
    const [highlight_color_klass, color_klass, mode_klass] = this.getColorModeKlass();
    return html`
      <a
        href="/${ET.lang}/${this.data?.domain_name}"
        data-post-message="${dataStr(makeEmptyScreenNavigate(`/${ET.lang}/${this.data?.domain_name}`))}"
        class="${classes(klass.creator_fan_intersection, {
          [mode_klass]: this.data.mode !== 'backdrop',
        })}"
      >
        ${htmlIf(
          html`<img
            class="${klass.creator_fan_intersection_backdrop}"
            src="${getResizedImage70WebpUrl(
              this.data.banner_img || this.defaultImg,
              this.data.is_mobile ? 250 : 400,
            )}"
            alt="thumbnail_background_img"
          />`,
          this.data.mode === 'backdrop',
        )}
        <div class="${classes(klass.creator_fan_intersection_wrapper, mode_klass)}">
          <div class="${classes(klass.creator_fan_intersection_summary)}">
            <div class="${klass.creator_fan_intersection_thumbnail}">
              <img
                src="${getResizedUrl({
                  url: this.data.thumbnail_img || MShopAppStoreCommunityConstantS.EMPTY_USER_IMG,
                  width: 48 * 2,
                  format: 'webp',
                  quality: 85,
                })}"
                alt=""
              />
            </div>
            <div class="${klass.creator_fan_intersection_contents}">
              <span
                class="${classes(klass.creator_fan_intersection_contents_heading, {
                  [highlight_color_klass]: this.data.mode === 'backdrop',
                })}"
              >
                ${new CreatorNameView({
                  size: this.data.is_mobile ? 'small' : 'medium',
                  name: html`<h3 class="${font('16_bold', '14_bold')}">
                    ${unEscaper(this.data.name || '')}
                  </h3>`,
                  is_partner: this.data.is_partner,
                })}
              </span>
              <div class="${klass.creator_fan_intersection_contents_wrapper}">
                ${htmlIf(this._makeFanAndRankHtml(), !!this.data.filter || !!this.data.ranking)}
                <div
                  class="${classes(
                    klass.creator_fan_intersection_contents_fan,
                    highlight_color_klass,
                    font('14_medium', '12_medium'),
                  )}"
                >
                  <div class="${klass.creator_fan_intersection_contents_fan_wapper}">
                    <div class="${klass.creator_fan_intersection_contents_fan_title} ${color_klass}">
                      <span>${ET('mps2::creator_summary::fan')}</span>
                    </div>
                    <div class="${klass.creator_fan_intersection_contents_fan_count}">
                      ${this.counter}${ET('mps2::creator_summary::like_count')}
                    </div>
                  </div>
                  <div class="${klass.creator_fan_intersection_divider}"></div>
                  <div class="${klass.creator_fan_intersection_contents_fan_wapper}">
                    <div class="${klass.creator_fan_intersection_contents_fan_title} ${color_klass}">
                      <span>${ET('mps2::creator_summary::fan_letter')}</span>
                    </div>
                    <div class="${klass.creator_fan_intersection_contents_fan_count}">
                      ${html.preventEscape(
                        ET('mps2::creator_summary::fan_letter_count', {
                          fan_letter_count: pipe(
                            humanReadableNumberV2.to(this.data.fan_letter_count || 0, ET.lang === 'kr'),
                            values,
                            join(''),
                          ),
                        }),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          ${this.makeCreatorSummaryInfoHtml()}

          <div class="${klass.creator_fan_intersection_buttons}">${this.button}</div>
        </div>
      </a>
    `;
  }

  makeCreatorSummaryInfoHtml() {
    const { mode = 'normal' } = this.data;
    return html`
      <div
        class="${classes(klass.creator_fan_intersection_cart, klass.full, font('14_medium', '12_medium'), {
          [klass.bright_60]: mode === 'normal',
          [klass.dim]: mode === 'backdrop',
        })}"
      >
        ${this._makeCreatorSummaryInfoText()}
      </div>
    `;
  }

  override _makeCreatorSummaryInfoText() {
    return this.isCount
      ? html.preventEscape(
          ET('mps2::creator_summary::normal', {
            cart_user_count: pipe(
              humanReadableNumberV2.to(this.data.cart_summary?.cart_user_count || 0, ET.lang === 'kr'),
              values,
              join(''),
            ),
            cart_product_count: pipe(
              humanReadableNumberV2.to(this.data.cart_summary?.cart_product_count || 0, ET.lang === 'kr'),
              values,
              join(''),
            ),
            name: unEscaper(this.data.name || ''),
            bold: font('14_bold', '12_bold'),
          }),
        )
      : html.preventEscape(
          ET('mps2::creator_summary::not_cart_summary', {
            name: this.data.name,
            bold: font('14_bold', '12_bold'),
          }),
        );
  }

  private _makeFanAndRankHtml() {
    const { ranking } = this.data;
    const [highlight_color_klass, color_klass] = this.getColorModeKlass();
    return html`
      <div
        class="${classes(
          klass.creator_fan_intersection_contents_creator,
          highlight_color_klass,
          font('14_medium', '12_medium'),
        )}"
      >
        ${htmlIf(
          html`
            <div class="${klass.creator_fan_intersection_contents_filter}">
              <span>${this.data.filter?.name}</span>
            </div>
          `,
          !!this.data.filter,
        )}
        ${htmlIf(html` <div class="${klass.creator_fan_intersection_divider}"></div>`, !!this.data.filter)}
        ${htmlIf(
          html` <div class="${klass.creator_fan_intersection_rank}">
            <div class="${klass.creator_fan_intersection_rank_prefix}">
              <div class="${color_klass}">${ET('creator_shop::realtime_ranking')}</div>
              <div>${this._makeRank()}</div>
            </div>
            ${new RankDiff({
              klass: klass.rank_diff,
              font: staticFont('unica_14_bold', 'unica_12_bold'),
              ranking,
            })}
          </div>`,
          !!this.data.ranking,
        )}
      </div>
    `;
  }

  private _makeRank() {
    const { ranking } = this.data;
    if (!ranking) {
      return html.preventEscape(
        ET('creator_shop::profile::rank', {
          rank: '-',
          klass: font('14_medium', '12_medium'),
        }),
      );
    }

    return html.preventEscape(
      ET('creator_shop::profile::rank', {
        rank: `<div class="${font('14_medium', '12_medium')}">${commify(ranking.current_rank)}</div>`,
        klass: font('14_medium', '12_medium'),
      }),
    );
  }
}
